import React, { useEffect, useRef } from "react";
import { pickColor, rgba } from "./internal/colorUtils";

export const key = "starfield-warp";

const DEFAULT_BG = "#020617";
const DEFAULT_STAR = "#e2e8f0";

const STAR_DENSITY = 0.0006;
const MIN_STARS = 80;
const MAX_STARS = 500;
const SPEED = 0.012;       // how fast stars travel toward the camera (z units/frame)
const FOCAL = 320;         // perspective scaling factor
const TRAIL_FADE = 0.18;   // background overlay alpha (lower = longer trails)

export default function StarfieldWarp({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const starsRef = useRef([]);
  const colorsRef = useRef([DEFAULT_BG, DEFAULT_STAR]);

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG),
      pickColor(colors, 1, DEFAULT_STAR),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;
    let primed = false;

    // Each star has a fixed (x, y) direction in camera space and a depth z
    // that ticks down toward 0; when it reaches the camera plane we respawn
    // it at maximum depth with a fresh random direction.
    const newStar = (initialDepth = false) => ({
      x: (Math.random() - 0.5) * 2,
      y: (Math.random() - 0.5) * 2,
      z: initialDepth ? Math.random() : 1,
    });

    const seedStars = () => {
      const target = Math.round(width * height * STAR_DENSITY);
      const count = Math.max(MIN_STARS, Math.min(MAX_STARS, target));
      const arr = new Array(count);
      for (let i = 0; i < count; i += 1) arr[i] = newStar(true);
      starsRef.current = arr;
    };

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seedStars();
      primed = false;
    };

    const tick = () => {
      const [bg, starColor] = colorsRef.current;
      const cx = width / 2;
      const cy = height / 2;

      if (!primed) {
        ctx.fillStyle = bg;
        ctx.fillRect(0, 0, width, height);
        primed = true;
      } else {
        ctx.fillStyle = rgba(bg, TRAIL_FADE);
        ctx.fillRect(0, 0, width, height);
      }

      const stars = starsRef.current;
      ctx.lineCap = "round";

      for (let i = 0; i < stars.length; i += 1) {
        const s = stars[i];
        const prevZ = s.z;
        s.z -= SPEED;
        if (s.z <= 0.02) {
          stars[i] = newStar(false);
          continue;
        }

        const k = FOCAL / s.z;
        const sx = cx + s.x * k;
        const sy = cy + s.y * k;
        const prevK = FOCAL / prevZ;
        const px = cx + s.x * prevK;
        const py = cy + s.y * prevK;

        // Off-screen? Respawn so the field stays evenly distributed.
        if (sx < -50 || sx > width + 50 || sy < -50 || sy > height + 50) {
          stars[i] = newStar(false);
          continue;
        }

        const depth = 1 - s.z; // 0 (far) → 1 (close)
        const alpha = 0.25 + depth * 0.75;
        const lineWidth = 0.4 + depth * 1.8;

        ctx.strokeStyle = rgba(starColor, alpha);
        ctx.lineWidth = lineWidth;
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(sx, sy);
        ctx.stroke();
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
